import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { Subscription, fromEvent, tap, throttleTime } from 'rxjs';
import { IsMobileDevice } from '../../../helper/mobile';
import { MenuItemDefinition } from '../../../model/menu-item-definition';
import { CommandDispatcher } from '../../../service/command/command-dispatcher.service';
import { CommandFactoryService } from '../../../service/factory/command-factory.service';

@Component({
    selector: 'app-context-menu',
    templateUrl: './context-menu.component.html',
    styleUrls: ['./context-menu.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ContextMenuComponent<TContext = any> implements OnInit, OnDestroy {
  constructor(
    private readonly _commandDispatcher: CommandDispatcher,
    private readonly _commandFactory: CommandFactoryService<TContext>
  ) {}

  private scrollSubscription: Subscription;
  private isMobile: boolean;

  ngOnInit(): void {
    if (this.alwaysVisible) {
      this.buttonVisible = true;
    }
    this.isMobile = IsMobileDevice();
  }

  ngOnDestroy(): void {
    this.scrollSubscription?.unsubscribe();
  }

  get hasShownItems(): boolean {
    return this.menuDefinition.some(item => this.showItem(item));
  }

  @Input() context: TContext;
  @Input() smaller: boolean;
  @Input() menuDefinition: MenuItemDefinition<TContext>[];
  /** Context-menu open-button is always visible */
  @Input() alwaysVisible: boolean;
  
  @Input() contextId?: string;
  /**
   * If true, the menu is anchored left side, and extends rightward (relative to the button),
   * If false, the menu is anchored right side, and extends leftward.
   */
  @Input() leftSide: boolean = false;

  @ViewChild('menuButton')
  protected menuButton: ElementRef<HTMLInputElement>;

  @ViewChild('menuContainer')
  protected menuContainer: ElementRef<HTMLInputElement>;

  buttonVisible: boolean;
  isOpened: boolean = false;
  openUpwards = false;

  @HostListener('mouseleave') onBlur() {
    this.cancelHide = false;
    setTimeout(() => {
      if (!this.cancelHide) {
        this.close();
      }
    }, 300);
  }

  @HostListener('mouseenter') onMouseEnter() {
    this.cancelHide = true;
  }

  //@HostListener('window:scroll')
  onWindowScroll(): void {
    if (!this.isOpened) {
      return;
    }
    if (this.isMobile) {
      if (!this.isHeightConstrained()) {
        this.close();
      }
    } else if (!this.alwaysVisible) {
      // close menu if button is scrolled up into the toolbar:
      const buttonTop = this.menuButton?.nativeElement?.getBoundingClientRect()?.top; // TODO: replace with intersect event
      if (buttonTop <= 100) {
        // TODO: 100px = hardcoded $toolbar-height (70px) + $toolbar-button-height (40px) css variables - 10px for safety.
        if (!this.isHeightConstrained()) {
          this.close();
          this.buttonVisible = false; // !IMPORTANT that this is ONLY done if this.alwaysVisible == false!
        }
      }
    }
  }

  private isHeightConstrained(): boolean {
    const maxHeight = window.innerHeight * 0.8; // TODO: 0.80vh is hardcoded max height of the context menu (see "ul.menu" css)
    const availableHeight = Math.min(this.getAvailableHeight() ?? maxHeight, maxHeight);
    const contextMenuHeight = this.calculateContextMenuHeight();
    const extraMarginOfError = 25; //px
    // ^This allows the user to scroll approx this distance past the end of the context menu before it auto-closes.
    // This prevents the menu from closing too aggressively when the user scrolls to see the last items in the menu.
    return contextMenuHeight + extraMarginOfError > availableHeight;
  }

  private cancelHide = false;
  isDispatchingCommand = false;

  onClicked(name: string, event: Event) {
    event.stopPropagation();

    // Set isDispatchingCommand to true when a command is being dispatched
    this.isDispatchingCommand = true;

    this.close();
    let command = this._commandFactory.createContextCommand(name, this.context, this.contextId);
    this._commandDispatcher.sendCommand(command);

    // Set isDispatchingCommand to false after the command has been dispatched
    // You can do this in a finally block or after a Promise resolves or rejects,
    // depending on how you're dispatching the command
    this.isDispatchingCommand = false;
  }

  public toggle(e: MouseEvent): void {
    e.stopPropagation();
    return this.isOpened ? this.close() : this.open();
  }

  public close(): void {
    this.isOpened = false;
    this.scrollSubscription?.unsubscribe();
    this.scrollSubscription = undefined;
  }

  public open(): void {
    if (!this.isOpened) {
      this.isOpened = true;
      this.handleMenuPosition();
      this.scrollSubscription?.unsubscribe();
      this.scrollSubscription = fromEvent(window, 'scroll')
        .pipe(
          throttleTime(200, undefined, { leading: false, trailing: true }),
          tap(() => this.onWindowScroll())
        )
        .subscribe();
    }
  }

  showItem(menuItem: MenuItemDefinition<TContext>): boolean {
    return !menuItem.disableItem?.(this.context);
  }

  private calculateContextMenuHeight(): number {
    var menuItemsCount = this.menuDefinition.length; // TODO: we shouldn't need to recalculate this every time
    const fontSize = parseFloat(getComputedStyle(this.menuContainer.nativeElement).fontSize);

    // These are the calculation that allow to get exact height.
    // Size of the line and margins are hardcoded and taken from css class 'menu-item'.
    // item height: (padding-bottom + padding-top + line-height (font-size * 1.5 ) + border)
    // context menu height: (number of elements * item height) - last element border + padding-top + padding-bottom + array height.
    //const menuItemHeight = ((3 * 2) + (fontSize * 1.5) + 0.8);
    //const contextMenuHeight = (menuItemsCount * menuItemHeight) - 0.8 + (10 * 2) + 9.5;

    // Having above calculations here is not ideal though.
    // Perhaps we should think about some 'pessimistic' value for line heights, so we get the right value for most cases.
    // We know line height is *1.5 + add paddings and borders is extra 0.425em = 1.925em.
    // Round to two decimals is 1.93em, and overall menu adds an additional 1.8em.
    // So perhaps we can approximate the calculations as ({number of items} * 1.93em) + 2em?
    const contextMenuHeightPessimistic = menuItemsCount * (fontSize * 1.93) + fontSize * 2;
    return contextMenuHeightPessimistic;
  }

  // Note: Now that c-menu pops-out of container bounds, this method has been simplified to not care about inner bounds.
  private calculateAvailableHeight(): { upwards: number; downwards: number } {
    const buttonRect = this.menuButton.nativeElement.getBoundingClientRect();
    const buttonBottom = buttonRect.top + buttonRect.height;

    const windowHeight = window.innerHeight;
    const toolbarHeight = 70; // TODO: hardcoded $toolbar-height css variable.
    const lowerVisibilityLimit = windowHeight;
    const upperVisibilityLimit = toolbarHeight;

    const availableHeightUpwards = buttonRect.top - upperVisibilityLimit;
    const availableHeightDownwards = lowerVisibilityLimit - buttonBottom;

    return { upwards: availableHeightUpwards, downwards: availableHeightDownwards };
  }

  private getAvailableHeight(): number {
    const a = this.calculateAvailableHeight();
    return this.openUpwards ? a?.upwards : a?.downwards;
  }

  private handleMenuPosition() {
    if (!this.isOpened) {
      this.openUpwards = false;
      return;
    }

    const availableHeight = this.calculateAvailableHeight();
    const contextMenuHeightPessimistic = this.calculateContextMenuHeight();

    const canDisplayUpwards = availableHeight.upwards > contextMenuHeightPessimistic;
    const canDisplayDownwards = availableHeight.downwards > contextMenuHeightPessimistic;

    this.openUpwards = !canDisplayDownwards && canDisplayUpwards;
  }
}
